import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import usePatchHook from '../../../customHooks/usePatchHook';
import APIS from '../../../constant/EndPoint';
import { PlusIcon } from '@heroicons/react/24/outline';
import { tw } from '@careinfo/tailwind';

// Define prop types explicitly
interface StatusSelectProps {
  id: string;
  value?: string;
  options: { name: string; code: string }[];
  slug: string;
}

const StatusSelect = React.memo(
  ({ id, value = '', options, slug }: StatusSelectProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selected, setSelected] = useState(value);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Use useMemo to optimize filtering
    const filteredOptions = useMemo(
      () =>
        options?.filter((option) =>
          option.name.toLowerCase().includes(searchTerm.toLowerCase())
        ),
      [options, searchTerm]
    );

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const { mutateAsync, isLoading: isPatchLoading } = usePatchHook({
      queryKey: ['status'],
    });

    // Use useCallback to prevent unnecessary re-renders
    const handleChangeLocation = useCallback(
      async (name: string | null, value: string | null, id: string) => {
        const url = `${APIS.STATUS}${id}/`;
        const formData = {
          receiver_status: value,
          receiver_status_name: name,
          slug: slug,
        };
        try {
          await mutateAsync({ url, formData, setError: () => {} });
        } catch (err) {
          console.error(err);
        }
      },
      [mutateAsync]
    );

    useEffect(() => {
      setSelected(value);
    }, [value]);

    return (
      <div
        key={id}
        ref={dropdownRef}
        style={{ position: 'relative', width: '200px' }}
      >
        <div className={tw`flex justify-between bg-white items-center`}>
          <input
            type='text'
            value={selected ?? ''}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setSelected(e.target.value);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder='Search status...'
            className={tw`focus:outline-none`}
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
          />
          {selected && (
            <PlusIcon
              className={tw`h-5 w-5 text-gray-400 rotate-45 cursor-pointer`}
              aria-hidden='true'
              onClick={(e) => {
                e.stopPropagation();
                setSelected('');
                setSearchTerm('');
                handleChangeLocation(null, null, id);
              }}
            />
          )}
        </div>
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              border: '1px solid #ccc',
              backgroundColor: '#fff',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            {filteredOptions.map((option) => (
              <div
                key={option.code}
                onClick={() => {
                  handleChangeLocation(
                    option?.name,
                    option?.code ?? option?.name,
                    id
                  );
                  setIsOpen(false);
                }}
                style={{
                  padding: '8px',
                  cursor: 'pointer',
                  backgroundColor:
                    selected === option.name ? '#f0f0f0' : '#fff',
                }}
              >
                {option.name}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.id === nextProps.id &&
      prevProps.value === nextProps.value &&
      prevProps.slug === nextProps.slug &&
      JSON.stringify(prevProps.options) === JSON.stringify(nextProps.options)
    );
  }
);

export default StatusSelect;
